import { Loader2, Pause, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PAUSE_REASON_FIELD_ID, PAUSE_REASON_MAX_LENGTH, PAUSE_REASON_MIN_LENGTH } from './Dashboard.constants';
import { styles } from './Dashboard.styles';
import type {
	PauseConfirmActionsProps,
	PauseConfirmPanelProps,
	PauseControlProps,
	PauseToggleButtonProps,
} from './Dashboard.types';

const ToggleButton = ({ paused, label, onClick }: PauseToggleButtonProps) => {
	const Icon = paused ? Play : Pause;

	return (
		<button type="button" onClick={onClick} className={paused ? styles.pauseTogglePaused : styles.pauseToggleRunning}>
			<Icon className={styles.pauseIcon} aria-hidden="true" />
			{label}
		</button>
	);
};

const ConfirmActions = ({ onConfirm, onCancel, disabled, isPending, confirmLabel }: PauseConfirmActionsProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'pauseControl' });

	return (
		<div className={styles.pauseActions}>
			<button type="button" onClick={onCancel} className={styles.pauseCancel}>
				{t('CANCEL')}
			</button>
			<button type="button" onClick={onConfirm} disabled={disabled} className={styles.pauseConfirm}>
				{isPending && <Loader2 className={styles.pauseSpinner} aria-hidden="true" />}
				{confirmLabel}
			</button>
		</div>
	);
};

const ConfirmPanel = ({
	reason,
	onReasonChange,
	onConfirm,
	onCancel,
	isPending,
	isValid,
	errorKey,
	confirmLabel,
}: PauseConfirmPanelProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'pauseControl' });

	return (
		<div className={styles.pausePanel}>
			<label htmlFor={PAUSE_REASON_FIELD_ID} className={styles.pauseLabel}>
				{t('REASON_LABEL')}
			</label>
			<textarea
				id={PAUSE_REASON_FIELD_ID}
				value={reason}
				onChange={(event) => onReasonChange(event.target.value)}
				maxLength={PAUSE_REASON_MAX_LENGTH}
				placeholder={t('REASON_PLACEHOLDER')}
				className={styles.pauseTextarea}
			/>
			{errorKey && (
				<p role="alert" className={styles.pauseError}>
					{t(errorKey)}
				</p>
			)}
			<ConfirmActions
				onConfirm={onConfirm}
				onCancel={onCancel}
				disabled={!isValid || isPending}
				isPending={isPending}
				confirmLabel={confirmLabel}
			/>
		</div>
	);
};

/**
 * Renders nothing without the pause permission, so nobody sees a button they cannot use.
 * The panel remembers which state it was opened for, so it closes itself once `paused` flips.
 */
export const PauseControl = ({ paused, canPause, isPending, errorKey, onConfirm, onReset }: PauseControlProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'pauseControl' });
	const [openedFor, setOpenedFor] = useState<boolean | null>(null);
	const [reason, setReason] = useState('');

	if (!canPause) return null;

	const actionLabel = paused ? t('RESUME_ACTION') : t('PAUSE_ACTION');
	const isValid = reason.trim().length >= PAUSE_REASON_MIN_LENGTH && reason.length <= PAUSE_REASON_MAX_LENGTH;

	const openConfirm = () => {
		onReset();
		setReason('');
		setOpenedFor(paused);
	};

	const cancel = () => {
		onReset();
		setOpenedFor(null);
		setReason('');
	};

	// The panel remembers the state it was opened for, so it closes on its own once the change lands.
	if (openedFor !== paused) return <ToggleButton paused={paused} label={actionLabel} onClick={openConfirm} />;

	return (
		<ConfirmPanel
			reason={reason}
			onReasonChange={setReason}
			onConfirm={() => onConfirm(reason.trim())}
			onCancel={cancel}
			isPending={isPending}
			isValid={isValid}
			errorKey={errorKey}
			confirmLabel={actionLabel}
		/>
	);
};
